import React, { useEffect, useRef, useState } from "react";
import {
  ChatMessage,
  ChatCompletionRequest,
  ChatCompletionResponse,
  ToolCall,
} from "../../model_gen/chat";
import { Config } from "../../model_gen/config";
import { formatTextOrJson, getMessageContentParts } from "../../utils/FormatUtils";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Card, CardContent } from "../ui/card";
import ConfigurationPanel from "../configuration/ConfigurationPanel";
import { ConfigurationPanelRef } from "../../types/IApplication";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { buildConfigFromChatRequest } from "./requestConfigMapping";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Tooltip } from "../ui/tooltip";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { cn } from "../../lib/utils";
import { 
  BotIcon, 
  UserIcon, 
  InfoIcon, 
  ClockIcon, 
  KeyIcon, 
  ShieldCheckIcon,
  TrashIcon,
  PlusIcon,
  CopyIcon,
  PencilIcon
} from "lucide-react";
import ConfidenceBadge from "../shared/ConfidenceBadge";
import { toast } from "sonner";
import ReasoningDisplay, { parseReasoningFromContent } from "../Finetune/Datasets/ReasoningDisplay";

function confidenceFromRequestMetadata(
  metadata: ChatCompletionRequest["metadata"] | undefined
): number | undefined {
  const raw = metadata?._maitai_request_meta;
  if (raw == null || raw === "") {
    return undefined;
  }
  let meta: Record<string, unknown> = {};
  if (typeof raw === "object" && !Array.isArray(raw)) {
    meta = raw as Record<string, unknown>;
  } else if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        meta = parsed as Record<string, unknown>;
      }
    } catch {
      return undefined;
    }
  }
  const rc = meta.response_confidence;
  if (typeof rc === "number" && Number.isFinite(rc)) {
    return rc;
  }
  if (typeof rc === "string") {
    const n = Number(rc);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function clampUnitInterval(n: number): number {
  if (Number.isNaN(n) || !Number.isFinite(n)) {
    return 0;
  }
  return Math.min(1, Math.max(0, n));
}

/** Parse stored confidence (number, numeric string, or 0–100 percentage). */
function parseTokenConfidence(raw: unknown): number {
  if (typeof raw === "number" && Number.isFinite(raw)) {
    let c = raw;
    if (c > 1 && c <= 100) {
      c = c / 100;
    }
    return clampUnitInterval(c);
  }
  if (typeof raw === "string") {
    const n = Number(raw.trim());
    if (Number.isFinite(n)) {
      let c = n;
      if (c > 1 && c <= 100) {
        c = c / 100;
      }
      return clampUnitInterval(c);
    }
  }
  return 0;
}

function parseRawLogprobToConfidence(raw: unknown): number {
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return clampUnitInterval(Math.exp(raw));
  }
  if (typeof raw === "string") {
    const n = Number(raw);
    if (Number.isFinite(n)) {
      return clampUnitInterval(Math.exp(n));
    }
  }
  return 0;
}

interface TokenWithAlternatives {
  token: string;
  confidence: number;
  alternatives?: { token: string; confidence: number }[];
}

function getCompletionTokenConfidences(
  response: ChatCompletionResponse | undefined
): TokenWithAlternatives[] | null {
  if (!response) {
    return null;
  }
  const norm = response.normalized_logprobs?.tokens;
  if (norm && norm.length > 0) {
    return norm.map((t) => ({
      token: t.token,
      confidence: parseTokenConfidence(t.confidence),
      alternatives: t.alternatives?.map((a) => ({
        token: a.token,
        confidence: parseTokenConfidence(a.confidence),
      })),
    }));
  }
  const raw = response.choices?.[0]?.logprobs?.content;
  if (raw && raw.length > 0) {
    return raw.map((item) => ({
      token: item.token,
      confidence: parseRawLogprobToConfidence(item.logprob),
      alternatives: item.top_logprobs?.map((a) => ({
        token: a.token,
        confidence: parseRawLogprobToConfidence(a.logprob),
      })),
    }));
  }
  return null;
}

function confidenceColorForToken(confidence: number): {
  bg: string;
  border: string;
  text: string;
} {
  const c = clampUnitInterval(confidence);
  if (c >= 0.9) {
    return {
      bg: "bg-green-50 dark:bg-green-900/20",
      border: "border-green-200 dark:border-green-800",
      text: "text-foreground",
    };
  }
  if (c >= 0.75) {
    return {
      bg: "bg-orange-50 dark:bg-orange-900/20",
      border: "border-orange-300 dark:border-orange-700",
      text: "text-orange-700 dark:text-orange-300",
    };
  }
  return {
    bg: "bg-red-50 dark:bg-red-900/20",
    border: "border-red-300 dark:border-red-700",
    text: "text-red-700 dark:text-red-300",
  };
}

function filterTokensToContent(
  displayText: string,
  tokens: TokenWithAlternatives[]
): TokenWithAlternatives[] {
  if (!displayText || tokens.length === 0) return [];

  const joined = tokens.map((t) => t.token).join("");
  const startIdx = joined.indexOf(displayText);
  if (startIdx < 0) {
    const trimmed = displayText.trim();
    if (!trimmed) return [];
    const trimStart = joined.indexOf(trimmed);
    if (trimStart < 0) return tokens;
    return filterTokensToContent(trimmed, tokens);
  }

  const endIdx = startIdx + displayText.length;
  const result: TokenWithAlternatives[] = [];
  let pos = 0;
  for (const t of tokens) {
    const tokenEnd = pos + t.token.length;
    if (tokenEnd <= startIdx || pos >= endIdx) {
      pos = tokenEnd;
      continue;
    }
    const sliceStart = Math.max(0, startIdx - pos);
    const sliceEnd = Math.min(t.token.length, endIdx - pos);
    const slicedToken = t.token.slice(sliceStart, sliceEnd);
    if (slicedToken) {
      result.push({
        token: slicedToken,
        confidence: t.confidence,
        alternatives: t.alternatives,
      });
    }
    pos = tokenEnd;
  }
  return result;
}

function formatTokenDisplay(token: string): string {
  return token
    .replace(/ /g, "\u00B7")
    .replace(/\n/g, "\u21B5\n")
    .replace(/\t/g, "\u2192\t");
}

function buildTokenTooltipContent(t: TokenWithAlternatives): React.ReactNode {
  const pct = Math.round(clampUnitInterval(t.confidence) * 100);
  const alts = (t.alternatives || []).filter(
    (a) => a.token !== t.token
  );

  return (
    <div className="text-xs space-y-1 max-w-[220px]">
      <div className="flex justify-between gap-3 font-mono font-semibold">
        <span className="truncate">&quot;{formatTokenDisplay(t.token)}&quot;</span>
        <span className="shrink-0">{pct}%</span>
      </div>
      {alts.length > 0 && (
        <div className="border-t border-border/40 pt-1 mt-1 space-y-0.5">
          <div className="text-muted-foreground text-[10px] uppercase tracking-wide">
            Alternatives
          </div>
          {alts.map((a, j) => {
            const aPct = Math.round(clampUnitInterval(a.confidence) * 100);
            return (
              <div key={`alt-${j}`} className="flex justify-between gap-3 font-mono">
                <span className="truncate">&quot;{formatTokenDisplay(a.token)}&quot;</span>
                <span className="shrink-0 text-muted-foreground">{aPct}%</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function renderTokenSegments(
  tokens: TokenWithAlternatives[]
): React.ReactNode {
  if (tokens.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-0.5">
      {tokens.map((t, i) => {
        const colors = confidenceColorForToken(t.confidence);
        const display = formatTokenDisplay(t.token);
        return (
          <Tooltip key={`tseg-${i}`} content={buildTokenTooltipContent(t)} immediate>
            <span
              className={cn(
                "inline-block border rounded px-1 py-0.5 text-xs font-mono whitespace-pre-wrap leading-tight cursor-default",
                colors.bg,
                colors.border,
                colors.text
              )}
            >
              {display}
            </span>
          </Tooltip>
        );
      })}
    </div>
  );
}

interface RequestOverviewProps {
  request: ChatCompletionRequest;
  response?: ChatCompletionResponse;
  isEditable?: boolean;
  onMessageEdit?: (index: number, content: string) => void;
  onMessageEditComplete?: (index: number, content: string) => void;
  onModelChange?: (model: string) => void;
  onRequestConfigChange?: (patch: Partial<Config>) => void;
  intentGroupId?: number;
  style?: React.CSSProperties;
  editingMessageIndex?: number | null;
  onEditingMessageChange?: (index: number | null) => void;
  editMode?: "all" | "final-assistant";
  showDetails?: boolean;
  expanded?: boolean;
  onToolCallChange?: (
    toolCallIndex: number,
    field: string,
    value: string
  ) => void;
  onAddToolCall?: () => void;
  onConfirmAddToolCall?: () => void;
  showAddToolCall?: boolean;
  newToolCall?: { name: string; arguments: string };
  setNewToolCall?: (toolCall: { name: string; arguments: string }) => void;
  setShowAddToolCall?: (show: boolean) => void;
  onDeleteToolCall?: (toolCallIndex: number) => void;
  annotationLabel?: any;
  editedToolCalls?: ToolCall[];
}

const RequestOverview: React.FC<RequestOverviewProps> = ({
  request,
  response,
  isEditable = false,
  onMessageEdit,
  onMessageEditComplete,
  onModelChange,
  onRequestConfigChange,
  intentGroupId,
  style,
  editingMessageIndex,
  onEditingMessageChange,
  editMode = "all",
  showDetails = true,
  expanded = false,
  onToolCallChange,
  onAddToolCall,
  onConfirmAddToolCall,
  showAddToolCall,
  newToolCall,
  setNewToolCall,
  setShowAddToolCall,
  onDeleteToolCall,
  editedToolCalls,
}) => {
  
  const containerRef = useRef<HTMLDivElement>(null);
  const [showTokens, setShowTokens] = useState(false);

  const messages = [
    ...(request?.params?.messages || []),
    ...(response?.choices?.[0]?.message ? [response.choices[0].message] : []),
  ];

  const completionTokenConfidences = getCompletionTokenConfidences(response);
  const hasLogprobs = completionTokenConfidences != null && completionTokenConfidences.length > 0;

  // Get reasoning from dataset request meta if available
  const getDatasetRequestReasoning = (): Record<string, string> | null => {
    try {
      const metaStr = request?.metadata?.["_maitai_dataset_request_meta"];
      if (metaStr && typeof metaStr === "string") {
        const meta = JSON.parse(metaStr);
        if (meta?.reasoning) {
          return meta.reasoning;
        }
      }
    } catch {
      // Ignore parse errors
    }
    return null;
  };
  const datasetRequestReasoning = getDatasetRequestReasoning();

  
  // Scroll to bottom when component mounts or messages change
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [request, response, messages.length]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Request ID copied to clipboard!");
    }).catch(() => {
      toast.error("Failed to copy request ID.");
    });
  };

  const renderRequestId = (requestId: string) => {
    // Show last 8 characters with ellipsis prefix for better readability
    const truncatedId = `...${requestId.slice(-8)}`;
    return (
      <div className="flex items-center gap-2">
        <code className="text-sm font-mono bg-muted px-2 py-1 rounded text-foreground">
          {truncatedId}
        </code>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0"
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(requestId);
          }}
        >
          <CopyIcon className="w-3 h-3" />
        </Button>
      </div>
    );
  };

  const getMessageIcon = (role: string) => {
    switch (role) {
      case "user":
        return <UserIcon className="w-4 h-4 text-green-600 dark:text-green-400" />;
      case "assistant":
        return <BotIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />;
      case "system":
        return <InfoIcon className="w-4 h-4 text-orange-600 dark:text-orange-400" />;
      default:
        return null;
    }
  };

  const getMessageStyle = (role: string) => {
    switch (role) {
      case "user":
        return "bg-green-50 border-green-200 dark:bg-green-900/10 dark:border-green-800";
      case "assistant":
        return "bg-blue-50 border-blue-200 dark:bg-blue-900/10 dark:border-blue-800";
      case "system":
        return "bg-orange-50 border-orange-200 dark:bg-orange-900/10 dark:border-orange-800";
      default:
        return "bg-muted/50 border-border";
    }
  };

  // Get all message indices for defaultValue when expanded
  const defaultActiveItems = expanded
    ? messages.map((_, index) => index.toString())
    : [messages.length - 1].map(String);

  // Function to determine if a message is editable
  const isMessageEditable = (index: number, message: ChatMessage) => {
    if (!isEditable) return false;

    if (editMode === "final-assistant") {
      return index === messages.length - 1 && message.role === "assistant";
    }

    // In "all" mode, allow editing any message if isEditable is true
    return true;
  };

  const isMessageEdited = (index: number) => {
    // Check for server-side edits
    const isLastMessage = index === (request.params.messages?.length || 0) - 1;
    return (
      isLastMessage &&
      (request.metadata?._maitai_annotation_label !== undefined ||
        request.metadata?._maitai_test_request_response !== undefined ||
        (request.metadata?._maitai_test_request_response_tool_calls &&
          (request.metadata?._maitai_test_request_response_tool_calls as any[])
            .length > 0))
    );
  };

  const getMessageContent = (message: ChatMessage, index: number) => {
    // Check for metadata overrides in the last message
    const isLastMessage = index === messages.length - 1;
    if (isLastMessage) {
      if (request.metadata?._maitai_annotation_label) {
        const annotationLabel =
          typeof request.metadata._maitai_annotation_label === "string"
            ? JSON.parse(request.metadata._maitai_annotation_label)
            : request.metadata._maitai_annotation_label;
        return annotationLabel.content;
      }
      const testResponse = request.metadata?._maitai_test_request_response as
        | string
        | undefined;
      if (testResponse !== undefined && testResponse !== null) {
        return testResponse;
      }
    }
    return message.content;
  };

  const getMessageToolCalls = (message: ChatMessage, index: number) => {
    const isLastMessage = index === messages.length - 1;
    
    if (isLastMessage && message.role === "assistant") {
      // First check if this is the response message (last message from response.choices[0].message)
      if (response?.choices?.[0]?.message?.tool_calls) {
        return response.choices[0].message.tool_calls;
      }
      
      // Check for test response tool calls
      if (request.metadata?._maitai_test_request_response_tool_calls) {
        return request.metadata._maitai_test_request_response_tool_calls as ToolCall[];
      }
      
      // Check for annotation label tool calls
      if (request.metadata?._maitai_annotation_label) {
        const annotationLabel =
          typeof request.metadata._maitai_annotation_label === "string"
            ? JSON.parse(request.metadata._maitai_annotation_label)
            : request.metadata._maitai_annotation_label;
        return annotationLabel.tool_calls;
      }
    }
    
    return message.tool_calls;
  };

  const formatToolCallArguments = (args: string) => {
    try {
      // Parse the JSON string and format it nicely
      const parsedArgs = JSON.parse(args);
      return JSON.stringify(parsedArgs, null, 2);
    } catch (e) {
      // If it's not valid JSON, return as-is
      return args;
    }
  };


  const [showConfigEdit, setShowConfigEdit] = useState(false);
  const [dialogConfig, setDialogConfig] = useState<Config>(() =>
    buildConfigFromChatRequest(request)
  );
  const [configPanelKey, setConfigPanelKey] = useState(0);
  const configPanelRef = useRef<ConfigurationPanelRef>(null);

  const handleOpenConfigEdit = () => {
    setDialogConfig(buildConfigFromChatRequest(request));
    setConfigPanelKey((k) => k + 1);
    setShowConfigEdit(true);
  };

  const handleConfigUpdate = (
    partial: Partial<Config>,
    loadingHandler?: (loading: boolean) => void | null,
    save?: boolean
  ) => {
    if (loadingHandler) {
      loadingHandler(false);
    }
    if (save) {
      if (onRequestConfigChange) {
        onRequestConfigChange(partial);
      } else if (onModelChange && partial.model !== undefined) {
        onModelChange(partial.model);
      }
      setDialogConfig((prev) => ({ ...prev, ...partial }));
      setShowConfigEdit(false);
    }
  };

  const handleApplyConfig = () => {
    const hadUnsaved = configPanelRef.current?.hasUnsavedChanges ?? false;
    configPanelRef.current?.saveChanges();
    if (!hadUnsaved) {
      setShowConfigEdit(false);
    }
  };

  const renderToolCalls = (message: ChatMessage, index: number) => {
    // Use edited tool calls if available, otherwise get tool calls from message or metadata
    const messageToolCalls = getMessageToolCalls(message, index);
    const toolCallsToRender = (editedToolCalls && editedToolCalls.length > 0) ? editedToolCalls : (messageToolCalls || []);
    
    if (!isMessageEditable(index, message)) {
      if (!toolCallsToRender.length) return null;
      return (
        <div className="mt-2 space-y-2">
          <p className="text-sm font-medium">Tool Calls:</p>
          {toolCallsToRender.map((toolCall: ToolCall, i: number) => (
            <div
              key={toolCall.id || i}
              className="border-b border-border pb-2 mb-2 last:border-b-0 last:pb-0 last:mb-0"
            >
              <div className="mb-1">
                <span className="text-sm font-medium text-foreground">Function:</span>{" "}
                <span className="text-sm text-foreground">{toolCall.function.name}</span>
              </div>
              <div>
                <span className="text-sm font-medium text-foreground">Arguments:</span>
                <pre className="mt-1 p-2 bg-muted rounded text-sm overflow-x-auto text-foreground">
                  {formatToolCallArguments(toolCall.function.arguments)}
                </pre>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="mt-2 space-y-2">
        <div className="flex justify-between items-center">
          <p className="text-sm font-medium text-foreground">Tool Calls</p>
          <Button size="sm" onClick={onAddToolCall || (() => {})}>
            <PlusIcon className="w-4 h-4 mr-1" />
            Add Tool Call
          </Button>
        </div>

        {toolCallsToRender.map((toolCall: ToolCall, toolIndex: number) => (
          <div
            key={toolCall.id || toolIndex}
            className="border-b border-border pb-2 mb-2 last:border-b-0 last:pb-0 last:mb-0"
          >
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm font-medium text-foreground">Function:</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDeleteToolCall?.(toolIndex)}
                className="text-destructive hover:text-destructive"
              >
                <TrashIcon className="w-4 h-4" />
              </Button>
            </div>
            <Input
              value={toolCall.function.name}
              onChange={(e) =>
                onToolCallChange?.(toolIndex, "name", e.target.value)
              }
              className="mb-2"
              placeholder="Function name"
            />
            <div className="mb-1">
              <span className="text-sm font-medium text-foreground">Arguments:</span>
            </div>
            <Textarea
              value={toolCall.function.arguments}
              onChange={(e) =>
                onToolCallChange?.(toolIndex, "arguments", e.target.value)
              }
              className="min-h-[80px] max-h-[200px]"
              placeholder="Function arguments"
            />
          </div>
        ))}

        {showAddToolCall && (
          <div className="border border-blue-200 rounded p-2 bg-blue-50/50 dark:bg-blue-900/10 dark:border-blue-800">
            <div className="mb-2">
              <span className="text-sm font-medium text-foreground">Function:</span>
              <Input
                value={newToolCall?.name || ""}
                onChange={(e) =>
                  setNewToolCall?.({ ...newToolCall!, name: e.target.value })
                }
                className="mt-1"
                placeholder="Function name"
              />
            </div>
            <div className="mb-2">
              <span className="text-sm font-medium text-foreground">Arguments:</span>
              <Textarea
                value={newToolCall?.arguments || ""}
                onChange={(e) =>
                  setNewToolCall?.({
                    ...newToolCall!,
                    arguments: e.target.value,
                  })
                }
                className="mt-1 min-h-[60px] max-h-[120px]"
                placeholder="Function arguments"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddToolCall?.(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={onConfirmAddToolCall || (() => {})}
                disabled={!newToolCall?.name.trim()}
              >
                Add
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
    <Card className="border-border rounded-lg mb-6" style={style}>
      {showDetails && (
        <CardContent className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Tooltip content="Model used for this request" className="w-full">
              <Card className="w-full text-center border-border relative">
                <CardContent className="p-6">
                  {isEditable && (onRequestConfigChange || onModelChange) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute top-2 right-2 h-7 w-7 p-0"
                      type="button"
                      onClick={handleOpenConfigEdit}
                    >
                      <PencilIcon className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <BotIcon className="w-6 h-6 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground mb-2 font-gc-reg">Model</p>
                  <p className="font-gc-med text-foreground">{request.params.model}</p>
                </CardContent>
              </Card>
            </Tooltip>

            <Tooltip content={response?.first_token_time ? "TTFT / Total response time" : "Time taken to process the request"} className="w-full">
              <Card className="w-full text-center border-border">
                <CardContent className="p-6">
                  <ClockIcon className="w-6 h-6 text-green-600 dark:text-green-400 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground mb-2 font-gc-reg">Time</p>
                  <p className="font-gc-med text-foreground">
                    {response?.first_token_time
                      ? `${response.first_token_time}ms / ${response.response_time}ms`
                      : response?.response_time !== undefined && response.response_time !== null
                        ? `${response.response_time}ms`
                        : "N/A"}
                  </p>
                </CardContent>
              </Card>
            </Tooltip>

            <Tooltip content="Number of tokens used" className="w-full">
              <Card className="w-full text-center border-border">
                <CardContent className="p-6">
                  <KeyIcon className="w-6 h-6 text-orange-600 dark:text-orange-400 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground mb-2 font-gc-reg">Tokens</p>
                  <p className="font-gc-med text-foreground">
                    {`${response?.usage?.prompt_tokens || 0} > ${
                      response?.usage?.completion_tokens || 0
                    }`}
                  </p>
                </CardContent>
              </Card>
            </Tooltip>

            <Tooltip content="Model confidence based on logprobs" className="w-full">
              <Card className="w-full text-center border-border">
                <CardContent className="p-6">
                  <ShieldCheckIcon className="w-6 h-6 text-purple-600 dark:text-purple-400 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground mb-2 font-gc-reg">Confidence</p>
                  <div className="flex justify-center">
                    {(() => {
                      const fromResponse = response?.normalized_logprobs?.response_confidence;
                      const rc =
                        fromResponse ?? confidenceFromRequestMetadata(request.metadata);
                      if (rc === undefined || rc === null) {
                        return <span className="font-gc-med text-foreground">N/A</span>;
                      }
                      return <ConfidenceBadge responseConfidence={rc} size="md" />;
                    })()}
                  </div>
                </CardContent>
              </Card>
            </Tooltip>
          </div>
        </CardContent>
      )}

      <div ref={containerRef}>
        <CardContent className={cn("p-8", showDetails && "pt-0")}>
          <Accordion
            type="multiple"
            defaultValue={defaultActiveItems}
            className="w-full"
          >
            {messages.map((message: ChatMessage, index) => (
              <AccordionItem
                key={index}
                value={index.toString()}
                className={cn(
                  "mb-4 rounded-lg border last:mb-0",
                  getMessageStyle(message.role)
                )}
              >
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <div className="flex items-center gap-2 w-full">
                    {getMessageIcon(message.role)}
                    <span className="font-gc-med capitalize">
                      {message.role}
                      {isMessageEdited(index) && "*"}
                    </span>
                    {message.role === "assistant" && index === messages.length - 1 && hasLogprobs && (
                      <div
                        className="flex items-center gap-1.5 ml-auto mr-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Label htmlFor="show-tokens" className="text-xs text-muted-foreground cursor-pointer select-none">
                          Show tokens
                        </Label>
                        <Switch
                          id="show-tokens"
                          checked={showTokens}
                          onCheckedChange={setShowTokens}
                          className="scale-75"
                        />
                      </div>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div
                    className={cn(
                      "whitespace-pre-wrap p-4 rounded bg-background/50 text-sm font-gc-reg text-foreground",
                      isMessageEditable(index, message) && "cursor-pointer hover:bg-background/80"
                    )}
                    onClick={() =>
                      isMessageEditable(index, message) &&
                      onEditingMessageChange?.(index)
                    }
                  >
                    {(() => {
                      const parts = getMessageContentParts(
                        getMessageContent(message, index)
                      );
                      if (parts.length === 0) {
                        return null;
                      }
                      return (
                        <div className="space-y-3">
                          {parts.map((part, partIndex) => {
                            if (part.type === "image") {
                              return (
                                <img
                                  key={`${part.value}-${partIndex}`}
                                  src={part.value}
                                  alt="Message attachment"
                                  className="max-w-full rounded-md border border-border"
                                />
                              );
                            }
                            const { reasoning: contentReasoning, cleanContent } = parseReasoningFromContent(part.value);
                            const reasoning = contentReasoning || (message.role === "assistant" && index === messages.length - 1 && datasetRequestReasoning) || null;
                            const isAssistantResponseBubble =
                              message.role === "assistant" && index === messages.length - 1;
                            const tokenConfidences = isAssistantResponseBubble
                              ? getCompletionTokenConfidences(response)
                              : null;

                            let coloredBody: React.ReactNode = null;
                            if (showTokens && tokenConfidences && !contentReasoning && cleanContent && isAssistantResponseBubble) {
                              const filtered = filterTokensToContent(cleanContent, tokenConfidences);
                              if (filtered.length > 0) {
                                coloredBody = renderTokenSegments(filtered);
                              }
                            }

                            return (
                              <div key={`text-${partIndex}`} className="space-y-3">
                                {reasoning && (
                                  <ReasoningDisplay reasoning={reasoning} />
                                )}
                                {coloredBody != null ? (
                                  <div>{coloredBody}</div>
                                ) : cleanContent ? (
                                  <div>{formatTextOrJson(cleanContent)}</div>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                  {renderToolCalls(message, index)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </div>
    </Card>

    {isEditable && (
      <Dialog open={showConfigEdit} onOpenChange={setShowConfigEdit}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-gc-med">Model configuration</DialogTitle>
          </DialogHeader>
          <ConfigurationPanel
            key={configPanelKey}
            ref={configPanelRef}
            config={dialogConfig}
            updateConfig={handleConfigUpdate}
            resetConfig={() => {}}
            readOnly={false}
            isModalOpen={true}
            allowSecondaryModel={true}
            intentGroupId={intentGroupId}
            disableClientInference={false}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" type="button" onClick={() => setShowConfigEdit(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleApplyConfig}>
              Apply
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )}
    </>
  );
};

export default RequestOverview;
